
import { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, Filter, X, Tag as TagIcon, Sparkles, PlusCircle } from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';
import { useAppSelector } from '@/hooks/useAppSelector';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import BulkActions from './BulkActions';
import CreateContactDialog from './CreateContactDialog';
import { CanPerformAction } from '@/components/permissions/CanPerformAction';

interface SearchCriteria {
  field: string;
  value: string;
}

export const ContactListControls = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [searchCriteria, setSearchCriteria] = useState<SearchCriteria[]>([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const debouncedSearch = useDebounce(searchQuery, 300);
  const selectedContacts = useAppSelector(state => state.contacts.selectedContacts);

  // Available fields for advanced search
  const searchableFields = [
    { label: 'Name', value: 'name' },
    { label: 'Email', value: 'email' },
    { label: 'Company', value: 'company' },
    { label: 'Type', value: 'type' },
    { label: 'Status', value: 'status' },
  ];

  const handleAddCriteria = () => {
    setSearchCriteria([...searchCriteria, { field: 'name', value: '' }]);
  };

  const handleRemoveCriteria = (index: number) => {
    const newCriteria = [...searchCriteria];
    newCriteria.splice(index, 1);
    setSearchCriteria(newCriteria);
  };

  const handleCriteriaChange = (index: number, field: string, value: string) => {
    const newCriteria = [...searchCriteria];
    newCriteria[index] = { field, value };
    setSearchCriteria(newCriteria);
  };

  useEffect(() => {
    // In a real app, this would trigger a search API call with both
    // the simple search query and advanced criteria
    console.log('Search query:', debouncedSearch);
    console.log('Search criteria:', searchCriteria);
  }, [debouncedSearch, searchCriteria]);

  return (
    <div className="space-y-6">
      {/* Search and Filter Controls */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-lg">
          <div className="relative group">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400 group-focus-within:text-blue-500 transition-colors duration-200" />
            <Input
              placeholder="Search contacts by name, email, company..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 pr-12 h-12 bg-white/80 backdrop-blur-sm border-slate-200/60 shadow-sm hover:shadow-md focus:shadow-lg focus:border-blue-300 transition-all duration-200 text-slate-700 placeholder:text-slate-400"
            />
            {searchQuery && (
              <button
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full p-1 transition-all duration-200"
                onClick={() => setSearchQuery('')}
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <CanPerformAction
            action="contact.create"
            fallback={null}
          >
            <Button
              onClick={() => setIsCreateDialogOpen(true)}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg shadow-blue-500/25 transition-all duration-200 hover:shadow-blue-500/30 hover:scale-105 border-0 h-12 px-6"
            >
              <PlusCircle className="h-4 w-4 mr-2" />
              Add Contact
            </Button>
          </CanPerformAction>

          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={`h-12 px-6 bg-white/80 backdrop-blur-sm border-slate-200/60 shadow-sm hover:shadow-md transition-all duration-200 ${showFilters || searchCriteria.length > 0
                  ? 'bg-blue-50/80 border-blue-200 text-blue-700 shadow-blue-100/50'
                  : 'hover:bg-slate-50/80 text-slate-600'
                  }`}
              >
                <Filter className="h-4 w-4 mr-2" />
                Advanced Search
                {searchCriteria.length > 0 && (
                  <Badge variant="secondary" className="ml-2 bg-blue-100 text-blue-700 border-blue-200">
                    {searchCriteria.length}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[420px] p-0 bg-white/95 backdrop-blur-sm border-slate-200/60 shadow-xl">
              <div className="p-6 space-y-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                      <Sparkles className="w-4 h-4 text-white" />
                    </div>
                    <h4 className="font-semibold text-slate-800">Advanced Search</h4>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAddCriteria}
                    className="text-blue-600 border-blue-200 hover:bg-blue-50"
                  >
                    Add Filter
                  </Button>
                </div>

                {searchCriteria.length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    <Filter className="w-8 h-8 mx-auto mb-3 text-slate-300" />
                    <p className="text-sm">No filters applied yet</p>
                    <p className="text-xs text-slate-400 mt-1">Click "Add Filter" to create search criteria</p>
                  </div>
                )}

                {searchCriteria.map((criteria, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 bg-slate-50/50 rounded-xl border border-slate-200/50">
                    <select
                      className="flex h-10 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm transition-colors focus:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-100"
                      value={criteria.field}
                      onChange={(e) => handleCriteriaChange(index, e.target.value, criteria.value)}
                    >
                      {searchableFields.map(field => (
                        <option key={field.value} value={field.value}>
                          {field.label}
                        </option>
                      ))}
                    </select>
                    <Input
                      placeholder="Enter value..."
                      value={criteria.value}
                      onChange={(e) => handleCriteriaChange(index, criteria.field, e.target.value)}
                      className="flex-1 h-10 border-slate-200 focus:border-blue-300 focus:ring-2 focus:ring-blue-100"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveCriteria(index)}
                      className="h-10 w-10 p-0 hover:bg-red-50 hover:text-red-600 text-slate-400"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}

                {searchCriteria.length > 0 && (
                  <div className="border-t border-slate-200/60 pt-4">
                    <div className="flex flex-wrap gap-2">
                      <span className="text-xs font-medium text-slate-500 mb-2">Active Filters:</span>
                      {searchCriteria.map((criteria, index) => (
                        <Badge key={index} variant="secondary" className="bg-blue-100/80 text-blue-700 border-blue-200/60">
                          <TagIcon className="w-3 h-3 mr-1" />
                          {searchableFields.find(f => f.value === criteria.field)?.label}: {criteria.value || 'Empty'}
                          <button
                            onClick={() => handleRemoveCriteria(index)}
                            className="ml-2 hover:text-red-600 transition-colors"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Bulk Actions Panel */}
      {/* {selectedContacts.length > 0 && (
        <div className="bg-gradient-to-r from-blue-50/80 via-indigo-50/50 to-purple-50/80 backdrop-blur-sm rounded-xl border border-blue-200/60 shadow-lg shadow-blue-500/10 p-6 transition-all duration-300 animate-in slide-in-from-top-2">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25">
                <span className="text-white font-bold text-sm">{selectedContacts.length}</span>
              </div>
              <div>
                <span className="text-lg font-semibold text-slate-800">
                  {selectedContacts.length} contact{selectedContacts.length === 1 ? '' : 's'} selected
                </span>
                <p className="text-sm text-slate-600">Choose an action to apply to selected contacts</p>
              </div>
            </div>
          </div>
          <BulkActions />
        </div>
      )} */}

      {/* Create Contact Dialog */}
      <CanPerformAction action="contact.create">
        <CreateContactDialog
          open={isCreateDialogOpen}
          onOpenChange={setIsCreateDialogOpen}
        />
      </CanPerformAction>
    </div>
  );
};
